import { FULL_DECK } from '../core/deck';
import { nextInt } from '../core/rng';
import { bestHand, winners } from '../core/eval';
import { COMBO_CARDS, liveComboCount } from '../range/combos';
import { applyRemoval } from '../range/removal';
import { buildAlias, sampleAlias } from './alias';
import { MeanAccumulator } from './ci';
import { handVsHandExact, handVsRangeExact, nCk } from './enumerate';
import { DEFAULT_EXACT_THRESHOLD, DEFAULT_MC_ITERATIONS, } from './types';
/** Draw `slots` distinct cards from the deck minus `dead`, using the Rng. Fresh array; `slots===0`→[]. */
const drawRunout = (rng, dead, slots) => {
    if (slots === 0)
        return [];
    const pool = FULL_DECK.filter((c) => !dead.has(c));
    // Partial Fisher–Yates: draw `slots` cards from the front of a shrinking pool.
    const out = [];
    for (let i = 0; i < slots; i++) {
        const j = i + nextInt(rng, pool.length - i);
        const tmp = pool[i];
        pool[i] = pool[j];
        pool[j] = tmp;
        out.push(pool[i]);
    }
    return out;
};
/** Max rejection retries when sampling non-colliding combos across ranges before skipping a trial. */
const MAX_REJECT = 200;
export class TsEquityProvider {
    rng;
    constructor(rng) {
        this.rng = rng;
    }
    handVsHand(hero, villain, board) {
        return { equity: handVsHandExact(hero, villain, board), ci95: 0 };
    }
    handVsRange(hero, range, board, opts = {}) {
        const slots = 5 - board.length;
        // Remove hero + board cards from the villain range once; every downstream path reuses it.
        const r = applyRemoval(range, [...hero, ...board]);
        const live = liveComboCount(r);
        // Ops estimate for the exact/MC gate: runouts drawn after board + hero + a villain combo (−2).
        const runouts = nCk(52 - board.length - 4, slots);
        const threshold = opts.exactThreshold ?? DEFAULT_EXACT_THRESHOLD;
        if (live === 0)
            return { equity: 0, ci95: 0 };
        if (live * runouts <= threshold) {
            return { equity: handVsRangeExact(hero, r, board), ci95: 0 };
        }
        // Monte Carlo.
        const rng = opts.rng ?? this.rng.fork('handVsRange');
        const iters = opts.iterations ?? DEFAULT_MC_ITERATIONS;
        const table = buildAlias(r);
        const acc = new MeanAccumulator();
        const staticDead = new Set([...hero, ...board]);
        for (let t = 0; t < iters; t++) {
            const vidx = sampleAlias(table, rng);
            const [a, b] = COMBO_CARDS[vidx];
            const villain = [a, b];
            const dead = new Set(staticDead);
            dead.add(a);
            dead.add(b);
            const runout = drawRunout(rng, dead, slots);
            const fb = runout.length === 0 ? board : [...board, ...runout];
            const winnersIdx = winners([bestHand(hero, fb), bestHand(villain, fb)]);
            // winnersIdx is over [hero, villain]: [0]=hero win, [0,1]=chop, [1]=villain win.
            acc.push(winnersIdx.includes(0) ? 1 / winnersIdx.length : 0);
        }
        return { equity: acc.mean(), ci95: acc.ci95() };
    }
    rangeVsRange(ranges, board, opts = {}) {
        const slots = 5 - board.length;
        const rng = opts.rng ?? this.rng.fork('rangeVsRange');
        const iters = opts.iterations ?? DEFAULT_MC_ITERATIONS;
        // Remove board cards from every range, then build an alias table per range.
        const tables = ranges.map((r) => buildAlias(applyRemoval(r, board)));
        const accs = ranges.map(() => new MeanAccumulator());
        const boardNums = board;
        for (let t = 0; t < iters; t++) {
            const used = new Set(boardNums);
            const holes = [];
            let ok = true;
            for (let ri = 0; ri < tables.length; ri++) {
                const tbl = tables[ri];
                if (tbl.n === 0) {
                    ok = false; // a range with no live combos can't be sampled - degenerate spot.
                    break;
                }
                let pair = null;
                for (let tries = 0; tries < MAX_REJECT; tries++) {
                    const idx = sampleAlias(tbl, rng);
                    const [a, b] = COMBO_CARDS[idx];
                    if (!used.has(a) && !used.has(b)) {
                        pair = [a, b];
                        used.add(a);
                        used.add(b);
                        break;
                    }
                }
                if (pair === null) {
                    ok = false; // couldn't find a non-colliding combo (tiny overlapping ranges) - skip trial.
                    break;
                }
                holes.push([pair[0], pair[1]]);
            }
            if (!ok)
                continue;
            const runout = drawRunout(rng, used, slots);
            const fb = runout.length === 0 ? board : [...board, ...runout];
            const winnersIdx = winners(holes.map((h) => bestHand(h, fb)));
            const share = 1 / winnersIdx.length;
            const winSet = new Set(winnersIdx);
            for (let ri = 0; ri < accs.length; ri++)
                accs[ri].push(winSet.has(ri) ? share : 0);
        }
        return accs.map((a) => ({ equity: a.mean(), ci95: a.ci95() }));
    }
}
//# sourceMappingURL=ts-provider.js.map